import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function NewUser() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: roles = [] } = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });
  const { data: depts = [] } = useQuery({ queryKey: ["departments"], queryFn: () => get("/departments") });

  const [form, setForm] = useState({
    name: "", email: "", password: "", confirmPassword: "", role_id: "", department_id: "", status: "active",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: (body) => post("/users", body),
    onSuccess: (_u, body) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      if (body.password) {
        toast.success("User account created");
      } else {
        toast.success("User account created", { description: "Default login password: Inew@123$WTP — they'll be asked to change it on first login." });
      }
      navigate("/settings/users");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not create user"),
  });

  const submit = () => {
    if (!form.name || !form.email || !form.role_id) {
      toast.error("Name, email and role are required");
      return;
    }
    if (form.password || form.confirmPassword) {
      if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
      if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }
    }
    const { confirmPassword, ...body } = form;
    if (!body.password) delete body.password;
    if (!body.department_id) delete body.department_id;
    create.mutate(body);
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="Add User" subtitle="Create a new account with sign-in access and a role." />

      <Section title="Account Details" className="mb-4">
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <Label>Full Name *</Label>
            <Input data-testid="field-name" className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <Label>Email *</Label>
            <Input data-testid="field-email" type="email" autoComplete="off" className="mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </div>
          <div>
            <Label>Role *</Label>
            <Select value={form.role_id} onValueChange={(v) => set("role_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-role_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department</Label>
            <Select value={form.department_id} onValueChange={(v) => set("department_id", v)}>
              <SelectTrigger className="mt-1" data-testid="field-department_id"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{depts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="Sign-in Password" className="mb-4">
        <p className="px-4 pt-4 text-sm text-muted-foreground">
          Optional. If left blank, the account gets a default temporary password and the user will be asked to set their own on first login.
        </p>
        <div className="grid grid-cols-2 gap-4 p-4">
          <div>
            <Label>Password</Label>
            <PasswordInput data-testid="field-password" autoComplete="new-password" className="mt-1"
              value={form.password} onChange={(e) => set("password", e.target.value)} />
          </div>
          <div>
            <Label>Confirm Password</Label>
            <PasswordInput data-testid="field-confirm-password" autoComplete="new-password" className="mt-1"
              value={form.confirmPassword} onChange={(e) => set("confirmPassword", e.target.value)} />
          </div>
        </div>
      </Section>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => navigate("/settings/users")}>Cancel</Button>
        <Button data-testid="submit-new-user" onClick={submit} disabled={create.isPending}>Create User</Button>
      </div>
    </div>
  );
}
